import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { SevenPointStar } from "@/components/Star";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DonateSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", amount: null });
  const timer = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing", amount: null });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/donations/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", amount: data.amount });
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      attempts += 1;
      if (attempts < 8) {
        timer.current = setTimeout(poll, 2000);
      } else {
        setState({ status: "pending", amount: null });
      }
    };
    poll();
    return () => clearTimeout(timer.current);
  }, [sessionId]);

  return (
    <main data-testid="donate-success-page" className="flex min-h-screen flex-col items-center justify-center bg-jordan-sand px-6 text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="flex max-w-xl flex-col items-center"
      >
        <SevenPointStar className="mb-10 h-16 w-16 animate-spin-slow text-jordan-red" />
        {state.status === "checking" && (
          <>
            <h1 className="font-display text-4xl font-black uppercase tracking-tight sm:text-6xl" data-testid="donate-success-title">
              Confirming…
            </h1>
            <p className="mt-6 text-jordan-mute">We are confirming your donation with Stripe.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <p className="mb-4 font-arabic text-3xl text-jordan-green">شكراً من القلب</p>
            <h1 className="font-display text-4xl font-black uppercase tracking-tight sm:text-6xl" data-testid="donate-success-title">
              Shukran{state.amount ? ` — $${state.amount}` : ""}!
            </h1>
            <p className="mt-6 max-w-md leading-relaxed text-jordan-mute" data-testid="donate-success-message">
              Your generosity keeps the madafa fire burning — funding events, culture and
              support for our community. A receipt is on its way to your inbox.
            </p>
          </>
        )}
        {(state.status === "pending" || state.status === "missing") && (
          <>
            <h1 className="font-display text-4xl font-black uppercase tracking-tight sm:text-6xl" data-testid="donate-success-title">
              Almost there
            </h1>
            <p className="mt-6 max-w-md leading-relaxed text-jordan-mute" data-testid="donate-success-message">
              Your payment is still being processed. If you completed checkout, it will
              confirm shortly — thank you for supporting the Hub.
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="donate-success-home-link"
          className="mt-12 border border-jordan-ink bg-jordan-ink px-8 py-4 font-display text-xs font-bold uppercase tracking-[0.25em] text-jordan-sand transition-colors duration-300 hover:bg-jordan-red hover:border-jordan-red"
        >
          Back to the Hub
        </Link>
      </motion.div>
    </main>
  );
}
